import { AutocompletePaperSlotPropsOverrides, Paper, PaperProps, Stack, styled, SvgIcon, Typography } from '@mui/material'
import { useContext } from 'react'
import { ICON_FONT_SIZE } from '../theme2/components/iconButtonStyles'
import { AutocompleteActionsContext } from './actionsFocus'
import type { ElementType } from 'react'

export type AutocompleteAction = {
  label: string
  icon?: ElementType
  onSelect: () => void
}

type AutocompleteActionsPaperProps = {
  actions: AutocompleteAction[]
  /** Closes the popup before the action's own surface opens; omit when the autocomplete is uncontrolled. */
  setIsOpen?: (isOpen: boolean) => void
  /** Caps the paper so the options scroll under the actions instead of pushing them out of view. */
  bounded?: boolean
} & PaperProps &
  AutocompletePaperSlotPropsOverrides

/** Autocomplete dropdown paper with actions pinned below the options.
 *
 * With `bounded`, the paper takes over bounding its own height: MUI caps the listbox alone, so a
 * paper carrying actions as well grows past what the popper positioned it for. */
export const AutocompleteActionsPaper = ({
  actions,
  setIsOpen,
  bounded = false,
  children,
  ...rest
}: AutocompleteActionsPaperProps) => {
  const actionsFocus = useContext(AutocompleteActionsContext)

  return (
    <ActionsPaperRoot bounded={bounded} {...rest}>
      <OptionsRegion bounded={bounded}>{children}</OptionsRegion>
      {actions.length > 0 && (
        <Paper ref={actionsFocus?.registerRegion} onKeyDown={actionsFocus?.onRegionKeyDown} onBlur={actionsFocus?.onRegionBlur}>
          <Stack>
            {actions.map((action) => (
              <ActionButton
                key={action.label}
                type="button"
                // Suppressing the mousedown keeps focus in the input, so the popup is still mounted
                // when the click lands. The action itself runs on click, which a key press raises too.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  setIsOpen?.(false)
                  action.onSelect()
                }}
              >
                {action.icon && (
                  <SvgIcon
                    component={action.icon}
                    inheritViewBox
                    sx={{ marginRight: (theme) => theme.spacing(2), color: 'fg.subtle', fontSize: ICON_FONT_SIZE }}
                  />
                )}
                <Typography color="text.brand.default" variant="body-regular">
                  {action.label}
                </Typography>
              </ActionButton>
            ))}
          </Stack>
        </Paper>
      )}
    </ActionsPaperRoot>
  )
}

const MAX_DROPDOWN_HEIGHT = '40vh'

const ActionsPaperRoot = styled(Paper, { shouldForwardProp: (prop) => prop !== 'bounded' })<{ bounded: boolean }>(
  ({ bounded }) =>
    bounded
      ? {
          display: 'flex',
          flexDirection: 'column',
          maxHeight: MAX_DROPDOWN_HEIGHT,
          overflow: 'hidden',
        }
      : {}
)

// The listbox caps itself at the full dropdown height, which would leave no room for the actions.
const OptionsRegion = styled('div', { shouldForwardProp: (prop) => prop !== 'bounded' })<{ bounded: boolean }>(({ bounded }) =>
  bounded
    ? {
        flex: '1 1 auto',
        minHeight: 0,
        overflowY: 'auto',
        '& .MuiAutocomplete-listbox': {
          maxHeight: 'none',
        },
      }
    : {}
)

const ActionButton = styled('button')(({ theme }) => ({
  height: '40px',
  // Vertical space is the row's own height; flex centring places the label within it.
  padding: theme.spacing(0, 4),
  backgroundColor: theme.palette.bg.surface.default,
  ':hover': {
    backgroundColor: theme.palette.bg.interactive.hover,
  },
  alignItems: 'center',
  cursor: 'pointer',
  display: 'flex',
  width: '100%',
  border: 'none',
}))
